import {
  BOARD_GAP,
  boundingRect,
  buildParentMap,
  layoutSnapPositions,
  pageSize,
  type Rect,
  rootOf,
  type SceneDocument,
  worldBox,
} from "@cva/shared";
import type { SnapGuide } from "./store";

/**
 * Smart guides: edges and centres of the page, the print guides (bleed,
 * safety margin), the document's guides and layout grid (when shown) and all
 * other elements on the page.
 */

interface SnapLine {
  position: number;
  /** Extent of the object the line belongs to (for drawing the guide). */
  from: number;
  to: number;
}

export interface SnapTargets {
  x: SnapLine[];
  y: SnapLine[];
}

export function collectTargets(
  doc: SceneDocument,
  pageIndex: number,
  exclude: ReadonlySet<string>,
  print: { bleedPx: number; safePx: number } | null,
  layout: { guides: boolean; grid: boolean } = { guides: false, grid: false },
): SnapTargets {
  const { width, height } = pageSize(doc, doc.pages[pageIndex]);
  const x: SnapLine[] = [
    { position: 0, from: 0, to: height },
    { position: width / 2, from: 0, to: height },
    { position: width, from: 0, to: height },
  ];
  const y: SnapLine[] = [
    { position: 0, from: 0, to: width },
    { position: height / 2, from: 0, to: width },
    { position: height, from: 0, to: width },
  ];
  if (print) {
    for (const offset of [-print.bleedPx, print.safePx]) {
      if (offset === 0) continue;
      x.push(
        { position: offset, from: 0, to: height },
        { position: width - offset, from: 0, to: height },
      );
      y.push(
        { position: offset, from: 0, to: width },
        { position: height - offset, from: 0, to: width },
      );
    }
  }
  const lines = layoutSnapPositions(doc, layout);
  for (const position of lines.x) x.push({ position, from: 0, to: height });
  for (const position of lines.y) y.push({ position, from: 0, to: width });
  const page = doc.pages[pageIndex];
  const parents = buildParentMap(doc);
  for (const id of page?.children ?? []) {
    if (exclude.has(id) || exclude.has(rootOf(id, parents))) continue;
    const element = doc.elements[id];
    if (!element || element.hidden) continue;
    const box = worldBox(doc, id, parents);
    if (!box) continue;
    const r = boundingRect(box);
    for (const position of [r.x, r.x + r.width / 2, r.x + r.width]) {
      x.push({ position, from: r.y, to: r.y + r.height });
    }
    for (const position of [r.y, r.y + r.height / 2, r.y + r.height]) {
      y.push({ position, from: r.x, to: r.x + r.width });
    }
  }
  return { x, y };
}

interface AxisResult {
  delta: number;
  guides: SnapGuide[];
}

function snapAxis(
  values: number[],
  lines: SnapLine[],
  threshold: number,
  axis: "x" | "y",
  span: { from: number; to: number },
): AxisResult {
  let best: { delta: number; distance: number } | null = null;
  for (const value of values) {
    for (const line of lines) {
      const delta = line.position - value;
      const distance = Math.abs(delta);
      if (distance <= threshold && (!best || distance < best.distance - 0.001))
        best = { delta, distance };
    }
  }
  if (!best) return { delta: 0, guides: [] };
  const guides: SnapGuide[] = [];
  const snapped = values.map((value) => value + best.delta);
  for (const line of lines) {
    if (snapped.some((value) => Math.abs(value - line.position) < 0.5)) {
      guides.push({
        axis,
        position: line.position,
        from: Math.min(line.from, span.from),
        to: Math.max(line.to, span.to),
      });
    }
  }
  return { delta: best.delta, guides };
}

/** Snaps a moving rectangle (edges and centre). */
export function snapRect(rect: Rect, targets: SnapTargets, threshold: number) {
  const xs = [rect.x, rect.x + rect.width / 2, rect.x + rect.width];
  const ys = [rect.y, rect.y + rect.height / 2, rect.y + rect.height];
  const dx = snapAxis(xs, targets.x, threshold, "x", { from: 0, to: 0 }).delta;
  const dy = snapAxis(ys, targets.y, threshold, "y", { from: 0, to: 0 }).delta;
  const final = { x: rect.x + dx, y: rect.y + dy, width: rect.width, height: rect.height };
  const guides = [
    ...snapAxis(
      xs.map((v) => v + dx),
      targets.x,
      0.01,
      "x",
      { from: final.y, to: final.y + final.height },
    ).guides,
    ...snapAxis(
      ys.map((v) => v + dy),
      targets.y,
      0.01,
      "y",
      { from: final.x, to: final.x + final.width },
    ).guides,
  ];
  return { dx, dy, guides };
}

/** Snaps single values (resizing an edge). */
export function snapValue(
  value: number,
  lines: SnapLine[],
  threshold: number,
  axis: "x" | "y",
  span: { from: number; to: number },
) {
  const result = snapAxis([value], lines, threshold, axis, span);
  return { value: value + result.delta, guides: result.guides };
}

/**
 * Snaps a page dragged over the board to the edges and centres of the other
 * pages (alignment) and next to them with the usual gap.
 */
export function snapPageRect(
  moving: Rect,
  others: readonly Rect[],
  threshold: number,
): { dx: number; dy: number } {
  const best = (candidates: number[]) => {
    let delta = 0;
    let distance = threshold;
    for (const candidate of candidates) {
      if (Math.abs(candidate) < distance) {
        distance = Math.abs(candidate);
        delta = candidate;
      }
    }
    return delta;
  };
  const xs: number[] = [];
  const ys: number[] = [];
  const mx = [moving.x, moving.x + moving.width / 2, moving.x + moving.width];
  const my = [moving.y, moving.y + moving.height / 2, moving.y + moving.height];
  for (const other of others) {
    const ox = [other.x, other.x + other.width / 2, other.x + other.width];
    const oy = [other.y, other.y + other.height / 2, other.y + other.height];
    for (const a of mx) for (const b of ox) xs.push(b - a);
    for (const a of my) for (const b of oy) ys.push(b - a);
    // Beside a page with the standard gap.
    xs.push(
      other.x + other.width + BOARD_GAP - moving.x,
      other.x - BOARD_GAP - moving.width - moving.x,
    );
    ys.push(
      other.y + other.height + BOARD_GAP - moving.y,
      other.y - BOARD_GAP - moving.height - moving.y,
    );
  }
  return { dx: best(xs), dy: best(ys) };
}
